import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

interface PartsOrder {
  id: string;
  order_number: string;
  status: string;
  shipping_method: string;
  order_type: string;
  ship_to_company: string;
  ship_to_name: string;
  ship_to_street: string;
  ship_to_city: string;
  ship_to_state: string;
  ship_to_zip: string;
  parcel_carrier: string;
  parcel_service_name: string;
  parcel_total_amount: number;
  parcel_tracking_number: string;
  parcel_shipment_id: string;
  parcel_label_data: any;
  total_amount: number;
  dealer_company: string;
  dealer_first_name: string;
  dealer_last_name: string;
  unit_count: number;
  created_at: string;
}

export default function PartsOrdersPage() {
  const [orders, setOrders] = useState<PartsOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<Record<string, string>>({});
  const [labelData, setLabelData] = useState<Record<string, Array<{ trackingNumber: string; labelBase64: string; packageIndex: number }>>>({});

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getPartsOrders();
      setOrders(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleGenerateLabel = async (order: PartsOrder) => {
    if (!order.parcel_carrier) {
      alert('This order has no parcel carrier selected. Cannot generate label.');
      return;
    }

    setActionLoading(prev => ({ ...prev, [order.id]: 'generating' }));
    try {
      const result = await api.createParcelShipment(order.id);
      if (result.success) {
        alert(`UPS shipment created!\nTracking: ${result.trackingNumber}\nLabels: ${result.labelCount}`);
        loadOrders();
      } else {
        alert(`Failed: ${result.error}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [order.id]: '' }));
    }
  };

  const handleFetchLabels = async (order: PartsOrder) => {
    setActionLoading(prev => ({ ...prev, [order.id]: 'loading' }));
    try {
      const result = await api.getParcelLabels(order.id);
      if (result.success && result.labels && result.labels.length > 0) {
        setLabelData(prev => ({ ...prev, [order.id]: result.labels! }));
      } else {
        alert(`No labels found: ${result.error || 'Unknown error'}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [order.id]: '' }));
    }
  };

  const handlePrintSingleLabel = (orderNumber: string, label: { labelBase64: string }, index: number, total: number) => {
    const byteChars = atob(label.labelBase64);
    const byteArr = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
    const blob = new Blob([byteArr], { type: 'image/gif' });
    const imgSrc = URL.createObjectURL(blob);
    const printWindow = window.open('', '_blank', 'width=816,height=528');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${orderNumber} - Label ${index + 1} of ${total}</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { background: #f0f0f0; display: flex; justify-content: center; padding: 20px; }
              .page { width: 8.5in; height: 11in; background: #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.2); }
              .label-area { width: 7.75in; height: 4.75in; margin: 0.375in auto 0 auto; display: flex; justify-content: center; align-items: center; overflow: hidden; }
              img { width: 7.75in; height: 4.75in; object-fit: contain; }
              button { position: fixed; top: 10px; right: 20px; padding: 10px 24px; font-size: 16px; background: #2563eb; color: #fff; border: none; border-radius: 6px; cursor: pointer; z-index: 10; }
              button:hover { background: #1d4ed8; }
              @media print {
                @page { margin: 0; size: letter; }
                body { background: none; padding: 0; display: block; }
                .page { box-shadow: none; }
                button { display: none; }
              }
            </style>
          </head>
          <body>
            <button onclick="window.print();">Print Label ${index + 1}</button>
            <div class="page">
              <div class="label-area">
                <img src="${imgSrc}" />
              </div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const handleMarkShipped = async (order: PartsOrder) => {
    if (!confirm(`Mark order ${order.order_number} as shipped?`)) return;

    setActionLoading(prev => ({ ...prev, [order.id]: 'shipping' }));
    try {
      await api.markShipped({
        order_id: order.id,
        tracking_number: order.parcel_tracking_number || undefined,
      });
      loadOrders();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [order.id]: '' }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Parts Orders</h1>
          <p className="text-gray-400 text-sm mt-1">
            Parts orders ready to ship via UPS/FedEx/USPS
          </p>
        </div>
        <button
          onClick={loadOrders}
          className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-300">
          {error}
        </div>
      )}

      {orders.length === 0 ? (
        <div className="text-center py-16">
          <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <p className="text-gray-500 text-lg">No parts orders ready to ship</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(order => {
            const isProcessing = !!actionLoading[order.id];
            const hasShipment = !!order.parcel_shipment_id;

            return (
              <div
                key={order.id}
                className="bg-gray-800 rounded-xl border border-gray-700 p-5"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Order Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-lg font-bold text-white">{order.order_number}</span>
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-900/50 text-yellow-300 border border-yellow-700">
                        PARTS
                      </span>
                      {order.parcel_carrier && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-900/50 text-blue-300 border border-blue-700">
                          {order.parcel_service_name || order.parcel_carrier}
                        </span>
                      )}
                      {hasShipment && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-900/50 text-green-300 border border-green-700">
                          LABEL READY
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-400 space-y-1">
                      <div>
                        <span className="text-gray-500">Dealer:</span>{' '}
                        {order.dealer_company || `${order.dealer_first_name} ${order.dealer_last_name}`}
                      </div>
                      <div>
                        <span className="text-gray-500">Ship to:</span>{' '}
                        {order.ship_to_street || order.ship_to_name || order.ship_to_company || 'N/A'},{' '}
                        {order.ship_to_city}, {order.ship_to_state} {order.ship_to_zip}
                      </div>
                      <div className="flex gap-4">
                        <span>
                          <span className="text-gray-500">Items:</span> {order.unit_count}
                        </span>
                        <span>
                          <span className="text-gray-500">Total:</span>{' '}
                          ${Number(order.total_amount || 0).toFixed(2)}
                        </span>
                        {order.parcel_total_amount && (
                          <span>
                            <span className="text-gray-500">Shipping:</span>{' '}
                            ${Number(order.parcel_total_amount).toFixed(2)}
                          </span>
                        )}
                      </div>
                      {order.parcel_tracking_number && (
                        <div>
                          <span className="text-gray-500">Tracking:</span>{' '}
                          <span className="text-indigo-400 font-mono">{order.parcel_tracking_number}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {!hasShipment ? (
                      <button
                        onClick={() => handleGenerateLabel(order)}
                        disabled={isProcessing || !order.parcel_carrier}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                      >
                        {actionLoading[order.id] === 'generating' ? (
                          <span className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Creating Shipment...
                          </span>
                        ) : (
                          'Generate UPS Label'
                        )}
                      </button>
                    ) : (
                      <>
                        {!labelData[order.id] ? (
                          <button
                            onClick={() => handleFetchLabels(order)}
                            disabled={isProcessing}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 disabled:opacity-50 transition-colors text-sm font-medium"
                          >
                            {actionLoading[order.id] === 'loading' ? 'Loading...' : 'Load Labels'}
                          </button>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {labelData[order.id].map((label, idx) => (
                              <button
                                key={idx}
                                onClick={() => handlePrintSingleLabel(order.order_number, label, idx, labelData[order.id].length)}
                                className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 transition-colors text-sm font-medium"
                              >
                                Print Label {idx + 1}
                              </button>
                            ))}
                            {labelData[order.id].length > 1 && (
                              <button
                                onClick={() => {
                                  labelData[order.id].forEach((label, idx) => {
                                    setTimeout(() => handlePrintSingleLabel(order.order_number, label, idx, labelData[order.id].length), idx * 500);
                                  });
                                }}
                                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors text-sm font-medium"
                              >
                                Print All ({labelData[order.id].length})
                              </button>
                            )}
                          </div>
                        )}
                        <button
                          onClick={() => handleMarkShipped(order)}
                          disabled={isProcessing}
                          className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-500 disabled:opacity-50 transition-colors text-sm font-medium"
                        >
                          {actionLoading[order.id] === 'shipping' ? 'Shipping...' : 'Mark Shipped'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
